import { Comment, NewReaction } from '@/types/models';
import { createSelector } from '@reduxjs/toolkit';
import { allCommentsSelectorOfCurrentMap } from '@/redux/comment/comment.selectors';
import { SIZE_OF_EMPTY_ARRAY } from '@/constants/common';
import {
  currentDrawerReactionIdSelector,
  currentSelectedSearchElement,
} from '../drawer/drawer.selectors';
import { currentModelIdSelector, modelsDataSelector } from '../models/models.selectors';
import { rootSelector } from '../root/root.selectors';

export const reactionsSelector = createSelector(rootSelector, state => state.reactions);

export const reactionsHighlightDataSelector = createSelector(
  reactionsSelector,
  reactions => reactions.highlight.data || [],
);

export const reactionsDataSelector = createSelector(
  reactionsSelector,
  reactions => reactions.data || {},
);

export const reactionsDataStateForCurrentModelSelector = createSelector(
  reactionsDataSelector,
  currentModelIdSelector,
  (state, currentModelId) => state[currentModelId],
);

export const reactionsLoadingSelector = createSelector(
  reactionsDataStateForCurrentModelSelector,
  state => state?.loading,
);

export const reactionsForCurrentModelSelector = createSelector(
  reactionsDataStateForCurrentModelSelector,
  state => state?.data || [],
);

export const reactionsByModelIdSelector = createSelector(
  [reactionsDataSelector, (_state, modelId: number): number => modelId],
  (state, modelId) => state[modelId]?.data || [],
);

export const currentDrawerReactionSelector = createSelector(
  reactionsForCurrentModelSelector,
  currentDrawerReactionIdSelector,
  (reactions, currentDrawerReactionId): NewReaction | undefined => {
    return reactions.find(reaction => reaction.id === currentDrawerReactionId);
  },
);

export const currentDrawerReactionCommentsSelector = createSelector(
  currentDrawerReactionSelector,
  allCommentsSelectorOfCurrentMap,
  (reaction, comments): Comment[] => {
    if (reaction) {
      return comments.filter(
        comment =>
          comment.type === 'REACTION' &&
          comment.modelId === reaction.model &&
          Number(comment.elementId) === reaction.id,
      );
    }
    return [];
  },
);

export const allHighlightReactionsSelector = createSelector(
  reactionsHighlightDataSelector,
  (highlightReactions): NewReaction[] => {
    return highlightReactions
      .map(({ data }) => data || [])
      .flat()
      .map(({ reaction }) => reaction);
  },
);

export const allHighlightReactionsSelectorOfCurrentMap = createSelector(
  allHighlightReactionsSelector,
  currentModelIdSelector,
  (highlightReactions, currentModelId): NewReaction[] => {
    return highlightReactions.filter(reaction => reaction.model === currentModelId);
  },
);

export const highlightReactionIdsForSelectedSearchElementSelector = createSelector(
  reactionsHighlightDataSelector,
  currentSelectedSearchElement,
  (highlightReactions, currentSearchElement): number[] => {
    const results = highlightReactions.find(
      ({ searchQueryElement }) => searchQueryElement === currentSearchElement,
    );
    if (results) {
      const reactionsSearchData =
        results?.data?.filter(searchModelElement => {
          return searchModelElement.reaction.id !== undefined;
        }) || [];
      return reactionsSearchData.map(highlightReactionDataState =>
        Number(highlightReactionDataState.reaction.id),
      );
    }
    return [];
  },
);

export const highlightReactionsPerModelSelector = createSelector(
  allHighlightReactionsSelector,
  modelsDataSelector,
  highlightReactionIdsForSelectedSearchElementSelector,
  (highlightReactions, models, reactionIdsForSelectedSearchElement) => {
    const highlightReactionsPerModelPerSearchElement = (models || []).map(model => {
      const highlightReactionsInGivenModel = highlightReactions.filter(
        highlightReaction =>
          model.id === highlightReaction.model &&
          reactionIdsForSelectedSearchElement.includes(highlightReaction.id),
      );

      return {
        modelName: model.name,
        modelId: model.id,
        numberOfReactions: highlightReactionsInGivenModel.length,
        highlightReactions: highlightReactionsInGivenModel,
      };
    });

    return highlightReactionsPerModelPerSearchElement.filter(
      model => model.numberOfReactions !== SIZE_OF_EMPTY_ARRAY,
    );
  },
);
